import { useState, useEffect, Dispatch } from 'react';

export type AsyncData<T> = {
    value: T;
    setValue: Dispatch<T>;
    loading: boolean;
    // Using any here because JavaScript does not enforce strong typing on catch()
    // for some reason.
    error: any | null;
};

/**
 * Custom hook for handling state, fetch and error handling for async function.
 * @param asyncFunc {Function} - Function for getting the requested async data
 * @param defaultState - The default state for the async data
 */
const useAsyncWithState = <T>(
    asyncFunc: Function | null,
    defaultState: T,
    refreshProps?: Array<any>,
): AsyncData<T> => {
    const [asyncState, setAsyncState] = useState(defaultState);
    const [isLoading, setIsLoading] = useState(asyncFunc !== null);
    const [error, setError] = useState<string | null>(null);

    const refresh = refreshProps !== undefined ? refreshProps : [asyncFunc !== null];

    useEffect(() => {
        const getData = async () => {
            if (asyncFunc) {
                setIsLoading(true);
                try {
                    const newData = await asyncFunc();
                    if (newData) {
                        setAsyncState(newData);
                        setError(null);
                    }
                } catch (e) {
                    if (e instanceof Error && e.message) {
                        setError(e.message);
                    } else if (e instanceof String) {
                        setError(e.toString());
                    }
                } finally {
                    setIsLoading(false);
                }
            }
        };
        getData();
    }, refresh);

    return {
        value: asyncState,
        setValue: setAsyncState,
        loading: isLoading,
        error,
    };
};

export default useAsyncWithState;
